<template>
  <div
    class="q-radio-group"
    :class="[
      `q-radio-group--${size}`,
      {
        'q-radio-group--disabled': disabled,
        'q-radio-group--vertical': vertical,
        'q-radio-group--horizontal': !vertical,
      },
    ]"
    role="radiogroup"
    :aria-label="ariaLabel"
  >
    <slot />
  </div>
</template>

<script setup>
import { provide, ref, computed, watch } from 'vue';

const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean],
    default: null,
  },
  name: {
    type: String,
    default: '',
  },
  size: {
    type: String,
    default: 'l',
    validator: (value) => ['l', 's'].includes(value),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  vertical: {
    type: Boolean,
    default: false,
  },
  ariaLabel: {
    type: String,
    default: 'Radio group',
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

// Reactive selected value
const selectedValue = ref(props.modelValue);

// Watch for external changes to modelValue
watch(
  () => props.modelValue,
  (newValue) => {
    selectedValue.value = newValue;
  },
);

// Update function for child radio components
const updateValue = (value) => {
  if (props.disabled) return; // Prevent updates when disabled
  selectedValue.value = value;
  emit('update:modelValue', value);
  emit('change', value);
};

// Provide context to child radio components
const radioGroupContext = {
  name: computed(() => props.name),
  size: computed(() => props.size),
  disabled: computed(() => props.disabled),
  selectedValue,
  updateValue,
};

provide('radioGroup', radioGroupContext);
</script>

<style lang="scss" scoped>
.q-radio-group {
  display: flex;
  gap: 16px;
  font-family: 'Inter', sans-serif;

  // Large size
  &--l {
    gap: 16px;
  }

  // Small size
  &--s {
    gap: 12px;
  }

  // Vertical layout
  &--vertical {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  // Horizontal layout
  &--horizontal {
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
  }

  // Disabled state
  &--disabled {
    opacity: 0.6;
    pointer-events: none;

    .q-radio {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  // Focus management for accessibility
  &:focus-within {
    outline: none;
  }

  // Responsive behavior
  @media (max-width: 768px) {
    &--horizontal {
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
    }
  }
}
</style>
